import CreateCompanyLayout from "../../../layouts/admin/companies/createCompany.layout";
import { useFormik } from "formik";
import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CompanyResult } from "src/interfaces/company";
import * as Yup from "yup";
import { createCompany } from "src/redux/companies/actions";
import { createLoadingSelector } from "src/redux/loading-reducer";
import { CompanyTypes } from "src/redux/companies/types";
import TyfhuSnack from "../../../components/TyfhuSnack";
import { SnackbarProps } from "src/interfaces/snackbar";

const schema = Yup.object().shape({
  company_name: Yup.string().required("Enter company name"),
  company_slug: Yup.string()
    .required("Enter company slug")
    .min(3, "Company slug must be at least 3 characters")
    .matches(/^[a-z0-9-]+$/, "Slug can only contain lowercase letters, numbers, and hyphens"),
  company_country: Yup.string().required("Select a country"),
  no_of_branches: Yup.number().required("Enter number of branches"),
  restaurant_type: Yup.string().required("Select restaurant type"),
  email: Yup.string().email().required("Please enter email"),
});

const CreateCompanyPage = () => {
  const dispatch = useDispatch();

  const { values, handleChange, errors, resetForm, validateForm } =
    useFormik({
      initialValues: {
        company_name: "",
        company_slug: "",
        company_country: "",
        no_of_branches: "",
        restaurant_type: "",
        email: "",
      } as any,
      validationSchema: schema,
      onSubmit: () => undefined,
    });
  const [result, setResult] = useState<CompanyResult>();
  const [snackbar, setSnackbar] = useState<SnackbarProps>({
    open: false,
    message: "",
    severity: "error",
  });

  const handleCloseSnack = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const loadingSelector = createLoadingSelector([CompanyTypes.ADD_COMPANY]);
  const isLoading = useSelector((state) => loadingSelector(state));

  const onPressCreate = useCallback(async () => {
    setResult(undefined);

    const formErrors = await validateForm();
    if (Object.keys(formErrors).length > 0) {
      return;
    }

    try {
      const data: any = await dispatch(createCompany(values));
      setResult(data?.data);
      setSnackbar({
        open: true,
        message: "Company created successfully!",
        severity: "success",
      });
      resetForm();
    } catch (err: any) {
      let errorMessage = "An error occurred while creating the company.";
      if (err) {
        if (err.errors && Array.isArray(err.errors) && err.errors.length > 0) {
          errorMessage = `${err.message || "Validation failed"}: ${err.errors.map((e: any) => e.message).join(", ")}`;
        } else if (err.message) {
          errorMessage = err.message;
        } else if (typeof err === "string") {
          errorMessage = err;
        }
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  }, [values, resetForm, dispatch, validateForm]);

  return (
    <>
      <CreateCompanyLayout
        isLoading={isLoading}
        values={values}
        errors={errors}
        handleChange={handleChange}
        onPressCreate={onPressCreate}
        result={result}
      />
      <TyfhuSnack snackbar={snackbar} handleClose={handleCloseSnack} />
    </>
  );
};

export default CreateCompanyPage;
